import React from "react";
import {Navigate, useLocation} from "react-router-dom";
import {LocalStorage} from "./LocalStorage";
import {PortalClientProvider} from "../context/PortalClientContext";

export const AgencyPrivateRoute = ({children}) => {
    const location = useLocation();
    const userType = LocalStorage.fetchUserType();
    let can_pass = LocalStorage.hasUserData() && (userType === 'AGENCY_USER' || userType === 'CLIENT_USER');
    let redirectTo = '/login';
    if (can_pass) {
        const agencyStatus = LocalStorage.fetchAgencyStatus();
        const isSubscriptionPage = location.pathname === '/agency/subscription';

        if (agencyStatus === "Never Active" && !isSubscriptionPage) {
            can_pass = false;
            redirectTo = '/free-trial-ended'
        }

        if (agencyStatus === "Churned" && !isSubscriptionPage) {
            can_pass = false;
            redirectTo = '/subscription-ended'
        }

        // Agencias MCP solo pueden ver la pantalla de upsell en "/"
        if (can_pass && LocalStorage.fetchIsDashboMcp() && location.pathname !== '/') {
            return <Navigate to="/" />;
        }

        // CLIENT_USER: redirigir al portal (excepto desde "/" que maneja Default.js)
        if (can_pass && userType === 'CLIENT_USER' && location.pathname !== '/') {
            return <Navigate to="/portal" replace />;
        }
    }

    if (!can_pass && redirectTo === '/login') {
        // Guardar la URL completa (path + query string) para redirigir después del login
        const fullUrl = location.pathname + location.search;
        LocalStorage.saveRedirectUrl(fullUrl);
    }

    return can_pass ? children : <Navigate to={redirectTo}/>;
};

/**
 * Para las rutas que usan LOS DOS tipos de usuario, como la vuelta del OAuth de Escalafy (`/escalafy/callback`,
 * un `redirect_uri` fijo para la agencia y el portal). Con `AgencyPrivateRoute` el usuario del portal se iba a
 * `/portal` y perdía `code` y `state`. Mismas guardas de la agencia (sesión, suscripción, MCP), sin esa.
 */
export const AgencyOrPortalPrivateRoute = ({children}) => {
    const location = useLocation();
    const userType = LocalStorage.fetchUserType();
    const isPortalUser = userType === 'CLIENT_USER';
    if (!LocalStorage.hasUserData() || (userType !== 'AGENCY_USER' && !isPortalUser)) {
        LocalStorage.saveRedirectUrl(location.pathname + location.search);
        return <Navigate to="/login"/>;
    }
    const agencyStatus = LocalStorage.fetchAgencyStatus();
    if (agencyStatus === "Never Active") return <Navigate to="/free-trial-ended"/>;
    if (agencyStatus === "Churned") return <Navigate to="/subscription-ended"/>;
    if (!isPortalUser && LocalStorage.fetchIsDashboMcp()) return <Navigate to="/"/>;
    return children;
};

export const PortalPrivateRoute = ({children}) => {
    const userType = LocalStorage.fetchUserType();
    const hasData = LocalStorage.hasUserData();

    if (!hasData) {
        return <Navigate to="/login" />;
    }

    if (userType !== 'CLIENT_USER') {
        return <Navigate to="/" />;
    }

    const agencyStatus = LocalStorage.fetchAgencyStatus();
    if (agencyStatus === "Never Active") return <Navigate to="/free-trial-ended" />;
    if (agencyStatus === "Churned") return <Navigate to="/subscription-ended" />;

    // Un solo punto de montaje cubre todas las rutas /portal/*; el preview de agencia
    // no pasa por acá y sigue resolviendo por PreviewContext.
    return <PortalClientProvider>{children}</PortalClientProvider>;
};

export const CRMPrivateRoute = ({children}) => {
    const isCRMUser = LocalStorage.hasUserData() && LocalStorage.fetchUserType() === 'CRM_USER';
    return isCRMUser ? children : <Navigate to="/crm/login"/>;
};
